import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Copy, Download } from 'lucide-react';
import { useBatteryStore } from '@/stores/batteryStore';
import { useDebounce, useCopyToClipboard } from '@/hooks';
import { Input, Button, Loading, ErrorMessage } from '@/components/common';
import { formatCurrency } from '@/utils/formatters';
import { generateCustomerMessage } from '@/utils/messageGenerator';
import { batteriesAPI } from '@/api/batteries';

export const SearchPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQuery = useDebounce(searchQuery, 300);
  const { searchResults, isSearching, error, searchBatteries } = useBatteryStore();
  const { copy } = useCopyToClipboard();

  // Check for battery parameter in URL on mount
  useEffect(() => {
    const batteryParam = searchParams.get('battery');
    if (batteryParam) {
      setSearchQuery(batteryParam);
      // Clear the URL parameter after setting it
      setSearchParams({});
    }
  }, []);

  useEffect(() => {
    if (debouncedQuery) {
      searchBatteries(debouncedQuery);
    }
  }, [debouncedQuery, searchBatteries]);

  const handleCopy = (battery: any) => {
    const message = generateCustomerMessage(battery);
    copy(message);
  };

  const handleExport = async () => {
    try {
      await batteriesAPI.downloadExcel(searchQuery || undefined);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-primary">البحث عن البطاريات</h1>
        {searchResults.length > 0 && (
          <Button onClick={handleExport} variant="secondary" className="gap-2">
            <Download className="h-5 w-5" />
            تصدير إلى Excel
          </Button>
        )}
      </div>

      <div className="card">
        <Input
          placeholder="ابحث عن البطارية (الماركة أو الحجم)..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          icon={<Search className="h-5 w-5" />}
        />
      </div>

      {isSearching && <Loading text="جاري البحث..." />}

      {error && <ErrorMessage message={error} />}

      {!isSearching && !error && searchResults.length > 0 && (
        <div className="card">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>الماركة</th>
                  <th>الحجم</th>
                  <th>الاسم التجاري</th>
                  <th>سعر القطعة</th>
                  <th>سعر الجملة</th>
                  <th>مخزن المستودع</th>
                  <th>مخزن السائقين</th>
                  <th>المخزون الكلي</th>
                  <th>الحالة</th>
                  <th>الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {searchResults.map((battery) => (
                  <tr key={battery.id}>
                    <td className="font-semibold">{battery.brand}</td>
                    <td className="font-medium">{battery.size}</td>
                    <td className="text-sm text-gray-400">
                      {battery.tradeName || '-'}
                    </td>
                    <td className="font-medium text-green-400">
                      {formatCurrency(battery.priceRetail)}
                    </td>
                    <td className="font-medium text-blue-400">
                      {formatCurrency(battery.priceWholesale)}
                    </td>
                    <td>
                      <span className="text-sm">
                        {battery.totalWarehouseStock || 0} قطعة
                      </span>
                    </td>
                    <td>
                      <span className="text-sm">
                        {battery.totalDriverStock || 0} قطعة
                      </span>
                    </td>
                    <td>
                      <span className="font-semibold">
                        {battery.totalStock} قطعة
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${
                        battery.totalStock === 0 ? 'badge-danger' :
                        battery.totalStock < 10 ? 'badge-warning' :
                        'badge-success'
                      }`}>
                        {battery.totalStock === 0 ? 'نفذ' :
                         battery.totalStock < 10 ? 'منخفض' :
                         'متوفر'}
                      </span>
                    </td>
                    <td>
                      <Button
                        size="sm"
                        onClick={() => handleCopy(battery)}
                        className="gap-2"
                      >
                        <Copy className="h-4 w-4" />
                        نسخ
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!isSearching && !error && searchQuery && searchResults.length === 0 && (
        <div className="card text-center py-12">
          <p className="text-gray-400">لا توجد نتائج للبحث "{searchQuery}"</p>
        </div>
      )}

      {!searchQuery && (
        <div className="card text-center py-12">
          <Search className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">ابدأ بالبحث عن البطاريات</p>
        </div>
      )}
    </div>
  );
};
